import { z } from 'zod';
import {
  PAGINATION_PAGE_SIZE_DEFAULT,
  PAGINATION_SORT_DIRECTION,
} from '~/constants';

import {
  type ListParsingCasesParams,
  PARSING_CASE_ORDER_BY_URL,
  PARSING_CASE_ORDER_BY_URL_TO_API,
} from '~/data/parsing-case';
import { type ParsingCaseOrderByUrl } from '~/data/parsing-case/orderBy';

export function isParsingCaseOrderByUrl(
  value: unknown,
): value is ParsingCaseOrderByUrl {
  return (
    typeof value === 'string' &&
    Object.hasOwn(PARSING_CASE_ORDER_BY_URL_TO_API, value)
  );
}

export const deliveryNoteReparsingV2ListDefaultValues = {
  limit: PAGINATION_PAGE_SIZE_DEFAULT,
  orderBy: 'createdOn',
  page: 0,
  sortOrder: 'DESC',
} as const;

/*
 * Validates the search parameters for the DNPv2 reparsing list page.
 *
 * Filter state (status, companies, dates, …) is now owned by the ephemeral
 * universal-filters store, so the URL only carries pagination, sorting, and the
 * currently opened case.
 */
export const zodDeliveryNoteReparsingV2ListSearchParamsSchema = z.object({
  caseId: z.uuid().optional().catch(undefined),
  limit: z
    .number()
    .int()
    .min(1)
    .max(500)
    .optional()
    .prefault(deliveryNoteReparsingV2ListDefaultValues.limit)
    .catch(deliveryNoteReparsingV2ListDefaultValues.limit),
  orderBy: z
    .enum(PARSING_CASE_ORDER_BY_URL)
    .optional()
    .prefault(deliveryNoteReparsingV2ListDefaultValues.orderBy)
    .catch(deliveryNoteReparsingV2ListDefaultValues.orderBy),
  page: z
    .number()
    .nonnegative()
    .optional()
    .prefault(deliveryNoteReparsingV2ListDefaultValues.page)
    .catch(deliveryNoteReparsingV2ListDefaultValues.page),
  savedFilterId: z.uuid().optional().catch(undefined),
  sortOrder: z
    .enum(PAGINATION_SORT_DIRECTION)
    .optional()
    .prefault(deliveryNoteReparsingV2ListDefaultValues.sortOrder)
    .catch(deliveryNoteReparsingV2ListDefaultValues.sortOrder),
});

/**
 * Maps the URL-owned pagination/sort params to the API param shape. Filter
 * params are merged in separately from the universal-filters payload.
 */
export function searchParamsToListParsingCasesParams(
  search: z.infer<typeof zodDeliveryNoteReparsingV2ListSearchParamsSchema>,
): ListParsingCasesParams {
  const { limit, orderBy, page, sortOrder } = search;

  return {
    limit,
    offset: page * limit,
    orderBy: PARSING_CASE_ORDER_BY_URL_TO_API[orderBy],
    returnTotalCount: true,
    sortOrder,
  };
}
